package dev.lingvo.site.ds

import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import kotlin.js.Date

/** How much of a server instant reaches the page. */
enum class LTimeStyle {
    STAMP,
    MINUTE,
    AGE,
}

private const val EM_DASH = "—"

/**
 * Formats a server instant without constructing a [Date] for the fixed timestamp shapes.
 *
 * Malformed input is returned as written so a diagnostic value is not hidden by a shared fallback.
 */
fun formatUtc(iso: String?, style: LTimeStyle = LTimeStyle.STAMP, absent: String = EM_DASH): String {
    val raw = iso?.trim().orEmpty()
    if (raw.isEmpty()) return absent
    return when (style) {
        LTimeStyle.STAMP -> sliceInstant(raw, 19) ?: raw
        LTimeStyle.MINUTE -> sliceInstant(raw, 16) ?: raw
        LTimeStyle.AGE -> age(raw)
    }
}

private fun sliceInstant(iso: String, chars: Int): String? {
    if (iso.length < chars) return null
    if (iso[4] != '-' || iso[7] != '-' || iso[10] != 'T' || iso[13] != ':') return null
    if (chars >= 19 && iso[16] != ':') return null
    return iso.take(chars).replace('T', ' ') + " UTC"
}

private const val MINUTE_MS = 60_000.0
private const val HOUR_MS = 60 * MINUTE_MS
private const val DAY_MS = 24 * HOUR_MS
private const val AGE_CEILING_MS = 30 * DAY_MS

/**
 * How far ahead of this browser a server timestamp may sit and still mean "now".
 *
 * Every one of these strings is a server time compared against a clock this code does not control.
 * Machine clocks disagree by seconds as a matter of course, and a row written a moment ago whose
 * timestamp lands a second ahead of the browser produced a negative age — which fell through to the
 * absolute-date branch and printed `03/09/2026` in a column of "just now" and "2 min ago". The one
 * row that looked like it came from somewhere else was the newest one, every time.
 *
 * Five minutes is well past any skew a synchronised machine shows and well short of anything a
 * person would call scheduled, so a genuinely future timestamp still prints as a date.
 */
private const val CLOCK_SKEW_TOLERANCE_MS = 5 * MINUTE_MS

/**
 * An age, counted and translated.
 *
 * **Counted, because the unit is.** These were Kotlin templates, so every one of them was English in
 * every locale — a reader whose interface was translated still met "3 d ago" on the deleted-keys
 * page and beside an API token's last use. The overview's own relative-time formatter had already
 * been fixed this way and argues the case: `tPlural` rather than a flat lookup, because a language
 * that needs a second form for two cannot express it in a `{count}` substitution alone.
 *
 * **Past the ceiling it hands over to the browser.** `iso.take(10)` prints the stored value, so the
 * sentence read "2026-09-28" wherever a person writes dates any other way. `toLocaleDateString`
 * already speaks the reader's language, which is the same choice the overview makes past a week.
 */
private fun age(iso: String): String {
    val at = Date.parse(iso)
    // A date prefix rather than the whole ISO string: an unparseable value is usually a timestamp in
    // a shape this does not know, and "2026-09-02" is readable where
    // "2026-09-02T14:03:11.482913Z" is noise in a row that expected three words.
    if (at.isNaN()) return iso.take(10).ifBlank { tr("ds.ds.lformat.recently", "recently") }
    val delta = Date.now() - at
    return when {
        delta < -CLOCK_SKEW_TOLERANCE_MS -> Date(at).toLocaleDateString()
        delta < 0 -> tr("ds.ds.lformat.just_now", "just now")
        delta < MINUTE_MS -> tr("ds.ds.lformat.just_now", "just now")
        delta < HOUR_MS -> tPlural(
            "ds.ds.lformat.relative.minutes",
            (delta / MINUTE_MS).toInt(),
            "{count} min ago",
            "{count} min ago",
        )
        delta < DAY_MS -> tPlural(
            "ds.ds.lformat.relative.hours",
            (delta / HOUR_MS).toInt(),
            "{count} h ago",
            "{count} h ago",
        )
        delta < AGE_CEILING_MS -> tPlural(
            "ds.ds.lformat.relative.days",
            (delta / DAY_MS).toInt(),
            "{count} d ago",
            "{count} d ago",
        )
        else -> Date(at).toLocaleDateString()
    }
}

fun dateLong(iso: String): String? =
    isoParts(iso)?.let { (day, month, year) -> "$day ${MONTHS_LONG[month]} $year" }

fun dateShort(iso: String): String? =
    isoParts(iso)?.let { (day, month, year) -> "$day ${MONTHS_SHORT[month]} $year" }

fun dateBadge(iso: String): String? =
    isoParts(iso)?.let { (day, month, _) -> "$day ${MONTHS_SHORT[month]}" }

private fun isoParts(iso: String): Triple<Int, Int, Int>? {
    if (iso.length < 10 || iso[4] != '-' || iso[7] != '-') return null
    val year = iso.substring(0, 4).toIntOrNull() ?: return null
    val month = iso.substring(5, 7).toIntOrNull() ?: return null
    val day = iso.substring(8, 10).toIntOrNull() ?: return null
    if (month !in 1..12 || day !in 1..31) return null
    return Triple(day, month - 1, year)
}

private val MONTHS_LONG = arrayOf(
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
)

private val MONTHS_SHORT = arrayOf(
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
)

/** Formats a count with groups of three digits and preserves the minus sign. */
fun formatCount(value: Int): String = groupThousands(value.toString())

fun formatCount(value: Long): String = groupThousands(value.toString())

// A `formatCount(value, singular, plural)` used to live here, appending an "s" for anything that
// was not exactly one. That is English's rule with a general-sounding name, which is the specific
// mistake `tPlural` exists to prevent — and it shipped on the job page, where a reader saw
// "5 strings" in an otherwise translated interface. It is gone rather than documented: the wrong
// spelling still compiling is what let it spread to eight call sites. Count things with
// `tPlural(key, n, "{count} string", "{count} strings")`, which asks the reader's own language.

private fun groupThousands(digits: String): String {
    val negative = digits.startsWith('-')
    val body = if (negative) digits.substring(1) else digits
    val grouped = body.reversed().chunked(3).joinToString(",").reversed()
    return if (negative) "-$grouped" else grouped
}
